import { defineStore } from 'pinia';
import { getWsToken } from '@/api/settings.api';

const DEFAULT_URL =
  import.meta.env.VITE_WEBSOCKET_URL || 'wss://pbxws-dev.dewasyscrm.com/ws';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

function log(...args) {
  console.log('[VoipSocket]', ...args);
}

/**
 * Generic WebSocket store for the voip socket server.
 * Use this for a single shared connection; register open/message handlers for different features
 * (e.g. agent status, future notifications). Handlers are called when the socket opens or when
 * a message is received (parsed as JSON). Scale by adding more registerMessageHandler callbacks
 * that filter by message type.
 *
 * Authentication: before connecting, the store fetches a short-lived HMAC token
 * from POST /api/ws/auth and appends it as ?token=xxx to the WebSocket URL.
 * The Go WS server validates the token to identify the user and register the
 * connection under their user_id, enabling targeted publishing.
 */
export const useVoipSocketStore = defineStore('voipSocket', {
  state: () => ({
    isConnected: false,
    _ws: null,
    _reconnectAttempts: 0,
    _openHandlers: [],
    _messageHandlers: [],
    _url: DEFAULT_URL,
    _wsToken: null,
    _tokenExpiresAt: 0,
    userChannel: null,
  }),

  actions: {
    _handleOpen() {
      log('Connected', this._url);
      this.isConnected = true;
      this._reconnectAttempts = 0;
      this._openHandlers.forEach((fn) => {
        try {
          fn(this._ws);
        } catch (err) {
          console.error('[VoipSocket] Open handler error:', err);
        }
      });
    },

    _handleMessage(event) {
      try {
        const data = JSON.parse(event.data);
        this._messageHandlers.forEach((fn) => {
          try {
            fn(data);
          } catch (err) {
            console.error('[VoipSocket] Message handler error:', err);
          }
        });
      } catch (err) {
        console.error('[VoipSocket] Parse message error:', err);
      }
    },

    _handleClose(ev) {
      log('Disconnected', { code: ev?.code, reason: ev?.reason });
      this.isConnected = false;
      this._ws = null;
      if (this._reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        this._reconnectAttempts++;
        setTimeout(() => this.connect(), RECONNECT_DELAY_MS);
      }
    },

    _handleError() {
      this.isConnected = false;
    },

    /**
     * Register a handler called when the socket opens. Receives the WebSocket instance.
     * @returns {Function} Unsubscribe function
     */
    registerOpenHandler(fn) {
      this._openHandlers.push(fn);
      return () => {
        this._openHandlers = this._openHandlers.filter((f) => f !== fn);
      };
    },

    /**
     * Register a handler called for every message (parsed JSON).
     * @returns {Function} Unsubscribe function
     */
    registerMessageHandler(fn) {
      this._messageHandlers.push(fn);
      return () => {
        this._messageHandlers = this._messageHandlers.filter((f) => f !== fn);
      };
    },

    /**
     * Fetch a short-lived auth token from the backend.
     * Returns the token string or null on failure.
     */
    async _fetchToken() {
      // Reuse token if still valid (with 30s buffer)
      if (this._wsToken && this._tokenExpiresAt > Date.now() + 30_000) {
        return this._wsToken;
      }
      try {
        const res = await getWsToken();
        const data = res?.data || res;
        this._wsToken = data.token;
        this._tokenExpiresAt = Date.now() + (data.expires_in || 300) * 1000;
        this.userChannel = data.channels?.user || null;
        log('Auth token obtained, user channel:', this.userChannel);
        return this._wsToken;
      } catch (err) {
        console.error('[VoipSocket] Failed to fetch WS auth token:', err);
        return null;
      }
    },

    /**
     * Connect to the WebSocket. Idempotent if already open or connecting.
     * Fetches an auth token first and appends it to the URL.
     * @param {Object} [options] Optional: { url } to override default URL
     */
    async connect(options = {}) {
      const baseUrl = options.url ?? this._url;
      const state = this._ws?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }

      // Fetch auth token before connecting
      const token = await this._fetchToken();
      const url = token
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
        : baseUrl;

      log('Connecting to', url.replace(/token=[^&]+/, 'token=***'));
      try {
        this._url = baseUrl;
        this._ws = new WebSocket(url);
        this._ws.onopen = () => this._handleOpen();
        this._ws.onmessage = (e) => this._handleMessage(e);
        this._ws.onclose = (ev) => this._handleClose(ev);
        this._ws.onerror = () => this._handleError();
      } catch (err) {
        console.error('[VoipSocket] Connect failed:', err);
        this.isConnected = false;
      }
    },

    /**
     * Send a JSON-serializable payload.
     */
    send(data) {
      if (this._ws?.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify(data));
      }
    },

    disconnect() {
      if (this._ws) {
        if (this._ws.readyState === WebSocket.OPEN) {
          this._ws.send(JSON.stringify({ type: 'disconnect' }));
        }
        this._ws.close();
        this._ws = null;
      }
      this.isConnected = false;
      this._reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    },
  },
});
